<template>
  <div class="freja-container">
    <BaseImage class="freja-image" :url="svgs.ICONS.FREJA" />
    <p class="freja-text mb-1">
      <strong>Med Freja eID</strong><br />
      Du identifierar dig i Freja-appen och godkänner att dela nödvändiga
      uppgifter (t.ex. namn och adress). Det gör ifyllnaden av formulär snabbare
      och minskar fel, samtidigt som din identitet verifieras på ett säkert
      sätt. Freja eID fungerar även för dig som saknar svenskt medborgarskap.
    </p>

    <BaseButton
      :disabled="requestQRCodeLoading || qrCodePopupVisible"
      :is-loading="requestQRCodeLoading || qrCodePopupVisible"
      is-fullwidth
      is-rounded
      @click="handleFrejaClick"
    >
      <p :style="{ 'margin-right': '10px' }">Freja eID</p>
      <BaseIconText
        class="info-modal-arrow-right"
        :icon-url="svgs.ICONS.ARROW_RIGHT"
      >
      </BaseIconText>
    </BaseButton>
    <p class="text-error">
      <span v-if="errorMessage">
        {{ errorMessage }}
      </span>
    </p>
    <DefaultModal v-if="emailModalVisible && !registeredEmail">
      <template slot="header">Registrera konto</template>
      <div slot="body" class="register-wrapper">
        <TermsModal v-if="termsModalVisible" @click="handleTermsModalClick" />
        <p>Hej och välkommen till Andrahand!</p>
        <p>För att komma igång, läs igenom och godkänn våra villkor.</p>
        <form class="form-wrapper" @submit.prevent="updateEmail">
          <Input
            v-model="email"
            :disabled="updatingEmail"
            placeholder="E-post"
            type="email"
            autofocus
            :validation="validateEmail"
            error-text=""
            class="mb-0 email"
            @input="errorMessage = null"
          />
          <Input
            v-model="emailConfirm"
            :disabled="updatingEmail"
            placeholder="Repetera e-post"
            type="email"
            :validation="validateEmailConfirm"
            error-text=""
            class="mb-0 email"
            @input="errorMessage = null"
          />
          <BaseCheckbox
            :checked="readTermsChecked"
            @change="readTermsChecked = !readTermsChecked"
            ><button
              type="button"
              class="accept-terms-btn"
              @click="termsModalVisible = true"
            >
              Jag accepterar Movitos policy
            </button></BaseCheckbox
          >
          <p v-show="errorMessage" class="text-error">{{ errorMessage }}</p>

          <BaseButton
            :is-loading="updatingEmail"
            :is-disabled="registrationBtnEnabled"
            :is-fullwidth="true"
            :is-rounded="true"
            type="submit"
            class="save-button"
            >Registrera</BaseButton
          >
        </form>

        <section
          v-if="errorMessage && emailExists"
          class="login-switch-wrapper mt-20 mb-20"
        >
          <button
            class="simple-button login-switcher"
            @click="$emit('switchLogin')"
          >
            Logga in med lösenord
          </button>
        </section>
      </div>
    </DefaultModal>
    <DefaultModal v-else-if="registeredEmail && !emailVerified">
      <template slot="header"> Verifiera e-post </template>
      <div slot="body" class="register-wrapper">
        <p v-if="registeredEmail && email">
          Vi behöver verifiera din e-post, därför har vi skickat ett mail till
          {{ email }} med en kod som du kan fylla i nedan.
        </p>

        <p v-if="registeredEmail && !email">
          Vi vet att du redan har ett konto, men vi skulle behöva verifiera din
          e-post. Därför har vi skickat ett mail till dig med en kod som du kan
          fylla i nedan
        </p>

        <form class="form-wrapper">
          <div class="otp-container">
            <div class="otp-input-wrapper">
              <OTP
                class="mb-0 otp-input"
                :digit-count="4"
                :is-disabled="verifyingEmail"
                :is-loading="verifyingEmail"
                @keydown="errorMessage = null"
                @completed="setOtpDigits"
              />
              <p v-show="errorMessage" class="otp-error">{{ errorMessage }}</p>
            </div>
          </div>
        </form>

        <div v-if="showSendNewVerificationButton" class="verify-footer">
          <BaseButton
            :is-rounded="true"
            type="button"
            @click="resendEmailVerification"
          >
            Skicka ny kod
          </BaseButton>
        </div>
      </div>
    </DefaultModal>
    <InfoModal
      v-else-if="status === FREJA_STATUS.APPROVED"
      :blur-bg="true"
      title="Vänlig vänta"
      :show-close-btn="false"
    >
      <div class="waiting-wrapper flex-center flex-col">
        <div class="success-container">
          <AnimatedCheckbox
            :show-animation="true"
            class="waiting-spinner"
            @done="login"
          />
        </div>
      </div>
    </InfoModal>
    <InfoModal
      v-else-if="qrCodePopupVisible"
      :blur-bg="true"
      title="Logga in med Freja eID"
      :show-close-btn="false"
    >
      <div class="waiting-wrapper flex-center flex-col">
        <div class="authenticating-container">
          <div class="waiting-wrapper flex-center flex-col">
            <div class="qr-container">
              <BaseLoadingSpinner
                v-if="status === FREJA_STATUS.DELIVERED_TO_MOBILE"
                class="qr-spinner"
              />
              <BaseImage :url="qrCodeImage" class="qr" />
            </div>
          </div>
          <ul style="text-align: left">
            <li>Öppna appen för Freja.</li>
            <li>Tryck på QR-symbolen i appen för Freja.</li>
            <li>Rikta kameran mot QR-koden.</li>
            <li>Följ instruktionerna i appen för att logga in.</li>
          </ul>
          <BaseButton
            :is-rounded="true"
            :is-default="true"
            class="cancel-auth-button"
            @click="cancelRequest"
            >Avbryt</BaseButton
          >
        </div>
      </div>
    </InfoModal>
  </div>
</template>

<script>
import applicationService from '@/services/applicationService';
import svgs from '@/utils/icon-constants';
import { validEmail } from '@/utils/input-validations';
import cognito from '@/services/cognito';
import { getCookie, removeCookie } from '@/utils/cookie';

import InfoModal from '@/components/InfoModal.vue';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';
import AnimatedCheckbox from '@/components/AnimatedCheckbox.vue';
import BaseImage from '@/components/BaseImage.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseIconText from '@/components/BaseIconText.vue';
import DefaultModal from '@/components/DefaultModal.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import Input from '@/components/Input.vue';
import TermsModal from '@/components/TermsModal.vue';
import OTP from '@/components/OTP.vue';
import auth from '@/services/auth';

export default {
  components: {
    InfoModal,
    BaseLoadingSpinner,
    AnimatedCheckbox,
    BaseImage,
    BaseButton,
    BaseIconText,
    DefaultModal,
    BaseCheckbox,
    Input,
    TermsModal,
    OTP
  },
  props: {
    storeName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      authRef: '',
      requestQRCodeLoading: false,
      pollTimeoutId: null,
      qrCodePopupVisible: false,
      status: '',
      qrCodeImage: '',
      svgs,
      termsModalVisible: false,
      readTermsChecked: false,
      updatingEmail: false,
      errorMessage: null,
      emailModalVisible: false,
      email: '',
      emailConfirm: '',
      registeredEmail: false,
      verifyingEmail: false,
      showSendNewVerificationButton: true,
      FREJA_STATUS: {
        STARTED: 'STARTED',
        DELIVERED_TO_MOBILE: 'DELIVERED_TO_MOBILE',
        CANCELED: 'CANCELED',
        RP_CANCELED: 'RP_CANCELED',
        EXPIRED: 'EXPIRED',
        APPROVED: 'APPROVED',
        REJECTED: 'REJECTED'
      }
    };
  },
  computed: {
    isMobileAgent() {
      return /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(
        navigator.userAgent
      );
    },
    registrationBtnEnabled() {
      if (
        this.readTermsChecked &&
        this.emailConfirm?.length > 0 &&
        this.email?.length > 0
      ) {
        return false;
      }
      if (this.updatingEmail) {
        return false;
      }
      return true;
    }
  },
  created() {
    const savedAuthRef = getCookie('authRef');
    if (savedAuthRef) {
      // When a user is redirected from freja app
      this.authRef = decodeURIComponent(savedAuthRef);
      removeCookie('authRef');
      this.pollAuthStatus();
    }
  },
  methods: {
    async verifyEmail() {
      this.verifyingEmail = true;

      if (!this.verificationOtpCode || this.verificationOtpCode.length !== 4) {
        this.errorMessage = 'Verifikationskod saknas eller är inte fullständig';
        this.verifyingEmail = false;
        return;
      }

      try {
        const result = await cognito.verifyEmail(
          this.userUuid,
          this.verificationOtpCode
        );

        if (result && this.email) {
          this.responseData.emailVerified = true;
        }
        await this.login();
      } catch (error) {
        if (error.response?.status === 406) {
          this.errorMessage = 'Koden är inte giltig, den kan vara för gammal.';
          this.showSendNewVerificationButton = true;
        } else {
          this.errorMessage = 'Ett fel har inträffat, vänligen försök igen.';
        }
        this.verifyingEmail = false;
        this.updatingEmail = false;
      }
    },
    setOtpDigits(digitStr) {
      this.errorMessage = null;
      this.verificationOtpCode = digitStr;
      this.verifyEmail();
    },
    async updateEmail() {
      this.updatingEmail = true;
      this.errorMessage = null;
      this.emailExists = false;

      if (!this.email) {
        this.errorMessage = 'Vänligen fyll i e-postadressen';
        this.updatingEmail = false;
        return;
      }

      const emailTrimmed = this.email.toLowerCase().trim();
      const confirmEmailTrimmed = this.emailConfirm.toLowerCase().trim();

      if (emailTrimmed !== confirmEmailTrimmed) {
        this.errorMessage = 'E-postadresserna är olika';
        this.updatingEmail = false;
        return;
      }

      try {
        this.responseData = await cognito.updateUserEmail(
          this.userUuid,
          emailTrimmed
        );

        this.registeredEmail = true;
        this.emailVerified = this.responseData.emailVerified;

        if (this.emailVerified) {
          await this.login();
        }
      } catch (error) {
        if (error.response?.status === 409) {
          this.errorMessage =
            'Det gick inte att skapa användaren, användaren finns redan. Prova logga in med lösenord, eller kontakta kundtjänst.';
          this.emailExists = true;
        } else if (error.response?.status === 400) {
          this.errorMessage = 'Adressen är inte giltig.';
        } else {
          this.errorMessage = 'Ett fel har inträffat, vänligen försök igen.';
        }

        this.updatingEmail = false;
      }
    },
    validateEmail(value) {
      return validEmail(value);
    },
    validateEmailConfirm(value) {
      return (
        validEmail(value) &&
        this.email?.toLowerCase().trim() ===
          this.emailConfirm?.toLowerCase().trim()
      );
    },
    handleTermsModalClick(eventType) {
      if (eventType === 'APPROVE') {
        this.readTermsChecked = true;
      }
      this.termsModalVisible = false;
    },
    pollAuthStatus() {
      const pollInterval = 1000;
      const maxAttempts = 60;
      let attempts = 0;

      const poll = async () => {
        const result = await applicationService.frejaPoll(this.authRef);

        if (result.status === this.FREJA_STATUS.DELIVERED_TO_MOBILE) {
          this.status = this.FREJA_STATUS.DELIVERED_TO_MOBILE;
        }
        if (result.status === this.FREJA_STATUS.APPROVED) {
          this.status = this.FREJA_STATUS.APPROVED;
        } else if (
          result.status === this.FREJA_STATUS.CANCELED ||
          result.status === this.FREJA_STATUS.RP_CANCELED ||
          result.status === this.FREJA_STATUS.REJECTED ||
          result.status === this.FREJA_STATUS.EXPIRED
        ) {
          if (
            [
              this.FREJA_STATUS.CANCELED,
              this.FREJA_STATUS.RP_CANCELED
            ].includes(result.status)
          ) {
            //alert('Inloggningen avbröts eller misslyckades.');
            this.errorMessage = 'Inloggningen avbröts.';
          } else if (result.status === this.FREJA_STATUS.EXPIRED) {
            this.errorMessage = 'Inloggningen har löpt ut.';
          } else if (result.status === this.FREJA_STATUS.REJECTED) {
            this.errorMessage = 'Inloggningen blev nekad.';
          } else {
            this.errorMessage = 'Ett okänt fel inträffade.';
          }
          this.cancelRequest();
        } else {
          attempts++;
          if (attempts < maxAttempts) {
            this.pollTimeoutId = setTimeout(poll, pollInterval);
          } else {
            this.qrCodePopupVisible = false;
          }
        }
      };

      poll();
    },

    async requestQRCode() {
      this.requestQRCodeLoading = true;
      // Avbryt eventuell tidigare polling
      if (this.pollTimeoutId) {
        clearTimeout(this.pollTimeoutId);
        this.pollTimeoutId = null;
      }

      const encodedAuthRef = await this.initFreja();

      const frejaURL = `frejaeid://bindUserToTransaction?transactionReference=${encodedAuthRef}`;
      const qrCodeURL = `https://resources.prod.frejaeid.com/qrcode/generate?qrcodedata=${encodeURIComponent(frejaURL)}`;

      this.qrCodeImage = qrCodeURL;
      this.qrCodePopupVisible = true;

      this.pollAuthStatus();
      this.requestQRCodeLoading = false;
    },
    handleFrejaClick() {
      this.errorMessage = null;
      if (this.isMobileAgent) {
        this.startFrejaMobile();
      } else {
        this.requestQRCode();
      }
    },
    async initFreja() {
      const res = await applicationService.frejaInit();
      this.authRef = res.authRef;
      return encodeURIComponent(this.authRef);
    },
    async startFrejaMobile() {
      const encodedAuthRef = await this.initFreja();
      const originAppScheme = `${window.location.origin}?authRef=${encodedAuthRef}`;
      const frejaURL = `frejaeid://bindUserToTransaction?transactionReference=${encodedAuthRef}&originAppScheme=${originAppScheme}`;
      window.location.href = frejaURL;
    },
    async cancelRequest() {
      // Avbryt eventuell tidigare polling
      if (this.pollTimeoutId) {
        clearTimeout(this.pollTimeoutId);
        this.pollTimeoutId = null;
        this.qrCodePopupVisible = false;
      }
      await applicationService.frejaCancel(this.authRef);
      this.qrCodePopupVisible = false;
      this.status = '';
    },
    async resendEmailVerification() {
      await cognito.resendVerification(this.userUuid);
      this.errorMessage = null;
      this.showSendNewVerificationButton = false;
    },
    async login() {
      const res = await applicationService.frejaLogin(this.authRef);
      const userId = res?.data?.userId;
      const email = res?.data?.email;
      const refreshToken = res?.data?.refreshToken;
      const emailVerified = res?.data?.emailVerified;

      this.userUuid = userId;

      if (res?.status === 202) {
        this.showEmailConfirm(email);
        return;
      }

      this.registeredEmail = true;
      this.emailVerified = emailVerified;

      if (this.emailVerified) {
        await auth.loginUserFreja({
          userId: userId,
          emailVerified: emailVerified,
          refreshToken: refreshToken
        });
        await this.initiate();
      } else if (res.success) {
        this.resendEmailVerification();
      }

      this.errorMessage = null;
    },
    showEmailConfirm(email) {
      this.email = email;
      this.emailConfirm = email;
      this.emailModalVisible = true;
      this.errorMessage = null;
    },
    async initiate() {
      return this.$store.dispatch(`${this.storeName}/initiate`, this.$router);
    }
  }
};
</script>

<style scoped>
.freja-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  background: white;
  box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  padding: 1.4rem 2rem 2rem;
  position: relative;
  overflow: hidden;
}
.qrCodePopup {
  display: flex;
  align-items: center;
  justify-content: center;
}

.waiting-wrapper {
  background-color: white;
  color: var(--color-gray);
  padding: 20px;
  border-radius: 10px;
}

.waiting-spinner {
  height: 50px;
  width: 50px;
}

.qr-spinner {
  height: 50px;
  width: 50px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.qr-spinner + .qr {
  opacity: 0.4;
}

.flex-center {
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
}

.flex-col {
  display: flex;
  flex-direction: column;
}

.authenticating-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 300px;
}

.cancel-auth-button {
  margin-bottom: 30px;
}

.success-container {
  height: 50px;
}

.freja-text {
  color: var(--color-gray);
}

.freja-image {
  margin-bottom: 2rem;
}
.qr-container {
  position: relative;
}

.form-wrapper {
  max-width: 500px;
}

.form-wrapper > *:not(:first-child) {
  margin-top: 10px;
}

.text-error {
  color: var(--color-error);
  margin-top: 10px;
}

.save-button {
  margin-top: 40px !important;
}

.login-switcher {
  font-size: 12px;
  display: flex;
  margin-top: 10px;
  color: var(--color-blue);
  align-items: center;
  gap: 0.5rem;
}

.login-switch-wrapper {
  margin-top: 15px;
  width: 100%;
}

.register-wrapper {
  max-width: 450px;
}
.register-wrapper p {
  margin-bottom: 20px;
}

.register-wrapper .verify-footer {
  display: flex;
  justify-content: space-evenly;
}

/* .email {
  :deep(input) {
    text-transform: lowercase;
  }

  :deep(::placeholder) {
    text-transform: none;
  }

  :deep(.valid:after) {
    background: none;
  }
} */

.mt-20 {
  margin-top: 2rem;
}

.mb-20 {
  margin-bottom: 2rem;
}

.accept-terms-btn {
  border: none;
  background: none;
  color: #000;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0;
}
.otp-container {
  text-align: center;
}

.otp-input {
  justify-content: center;
}

.otp-input-wrapper {
  position: relative;
  padding-bottom: 40px;
}

.otp-error {
  margin: 0;
  position: absolute;
  color: var(--color-error);
  font-size: 1.6rem;
  margin: 3px 0 0;
  text-align: center;
  left: 0;
  right: 0;
}
</style>
